/**
 * SecurityDashboard Component
 * Displays security metrics and integrity status for audit logs
 */

import React, { useState, useEffect } from 'react';
import { Shield, AlertTriangle, CheckCircle, Eye, Users, Activity, Lock } from 'lucide-react';
import { createSecuritySummary, validateLogChain } from '../../utils/logSecurity';
import { useTheme, getThemeStyles } from '../../contexts/ThemeContext';
import { usePermissions, hasPermission, PERMISSIONS } from '../../utils/permissions';

/**
 * SecurityDashboard component
 * @param {Object} props
 * @param {Array} props.logs - Array of log entries to analyze
 * @param {boolean} props.isVisible - Whether the dashboard is visible
 * @param {Function} props.onClose - Function to close the dashboard
 */
const SecurityDashboard = ({ logs = [], isVisible = false, onClose }) => {
  const { isDarkMode } = useTheme();
  const themeStyles = getThemeStyles(isDarkMode);
  const { permissions, loading: permissionsLoading } = usePermissions();
  const [securitySummary, setSecuritySummary] = useState(null);
  const [integrityResults, setIntegrityResults] = useState(null);
  const [loading, setLoading] = useState(false);

  // Check if user has permission to view security dashboard
  const canViewSecurity = hasPermission(permissions, PERMISSIONS.VIEW_ALL_LOGS) || 
                         hasPermission(permissions, PERMISSIONS.MODIFY_LOG_SETTINGS);

  useEffect(() => {
    if (isVisible && logs.length > 0) {
      setLoading(true);
      
      try {
        // Generate security summary
        const summary = createSecuritySummary(logs);
        setSecuritySummary(summary);
        
        // Run integrity check
        const integrity = validateLogChain(logs);
        setIntegrityResults(integrity);
      } catch (error) {
        console.error('Error analyzing logs for security:', error);
      } finally {
        setLoading(false);
      }
    }
  }, [isVisible, logs]);

  if (!isVisible) return null;

  // Check permissions
  if (permissionsLoading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${themeStyles.bg.primary} rounded-lg shadow-xl p-8`}>
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <span className={themeStyles.text.primary}>Loading permissions...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!canViewSecurity) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`${themeStyles.bg.primary} rounded-lg shadow-xl p-8 text-center`}>
          <Lock className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h3 className={`text-lg font-semibold ${themeStyles.text.primary} mb-2`}>
            Access Denied
          </h3>
          <p className={`${themeStyles.text.secondary} mb-4`}>
            You don't have permission to view the security dashboard.
          </p>
          <button
            onClick={onClose}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // eslint-disable-next-line no-unused-vars
  const getIntegrityStatusColor = (status) => {
    switch (status) {
      case 'valid':
        return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900';
      case 'compromised':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900';
      default:
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'error':
        return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900';
      case 'warning':
        return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900';
      default:
        return 'text-blue-600 bg-blue-100 dark:text-blue-400 dark:bg-blue-900';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className={`${themeStyles.bg.primary} rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-hidden`}>
        {/* Header */}
        <div className={`${themeStyles.bg.secondary} px-6 py-4 border-b ${themeStyles.border.primary}`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Shield className={`w-6 h-6 ${themeStyles.text.accent}`} />
              <h2 className={`text-xl font-semibold ${themeStyles.text.primary}`}>
                Security Dashboard
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`${themeStyles.text.muted} hover:${themeStyles.text.primary} transition-colors`}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className={`ml-3 ${themeStyles.text.secondary}`}>Analyzing security...</span>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Overview Cards */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Integrity Status */}
                <div className={`${themeStyles.bg.secondary} ${themeStyles.border.primary} rounded-lg border p-4`}>
                  <div className="flex items-center gap-3">
                    {securitySummary?.integrityStatus === 'valid' ? (
                      <CheckCircle className="w-8 h-8 text-green-500" />
                    ) : securitySummary?.integrityStatus === 'compromised' ? (
                      <AlertTriangle className="w-8 h-8 text-red-500" />
                    ) : (
                      <Eye className="w-8 h-8 text-yellow-500" />
                    )}
                    <div>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Integrity Status</p>
                      <p className={`text-lg font-semibold ${
                        securitySummary?.integrityStatus === 'valid' ? 'text-green-600 dark:text-green-400' :
                        securitySummary?.integrityStatus === 'compromised' ? 'text-red-600 dark:text-red-400' :
                        'text-yellow-600 dark:text-yellow-400'
                      }`}>
                        {securitySummary?.integrityStatus?.toUpperCase() || 'UNKNOWN'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Total Logs */}
                <div className={`${themeStyles.bg.secondary} ${themeStyles.border.primary} rounded-lg border p-4`}>
                  <div className="flex items-center gap-3">
                    <Activity className={`w-8 h-8 ${themeStyles.text.accent}`} />
                    <div>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Total Logs</p>
                      <p className={`text-lg font-semibold ${themeStyles.text.primary}`}>
                        {securitySummary?.totalLogs || 0}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Active Users */}
                <div className={`${themeStyles.bg.secondary} ${themeStyles.border.primary} rounded-lg border p-4`}>
                  <div className="flex items-center gap-3">
                    <Users className={`w-8 h-8 ${themeStyles.text.accent}`} />
                    <div>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Active Users</p>
                      <p className={`text-lg font-semibold ${themeStyles.text.primary}`}>
                        {Object.keys(securitySummary?.userActivity || {}).length}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Integrity Details */}
              {integrityResults && (
                <div className={`${themeStyles.bg.secondary} ${themeStyles.border.primary} rounded-lg border p-4`}>
                  <h3 className={`text-lg font-semibold ${themeStyles.text.primary} mb-4`}>
                    Integrity Analysis
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="text-center">
                      <p className={`text-2xl font-bold text-green-600 dark:text-green-400`}>
                        {integrityResults.validLogs}
                      </p>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Valid Logs</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold text-red-600 dark:text-red-400`}>
                        {integrityResults.invalidLogs}
                      </p>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Invalid Logs</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold text-yellow-600 dark:text-yellow-400`}>
                        {integrityResults.errors.length}
                      </p>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Errors</p>
                    </div>
                    <div className="text-center">
                      <p className={`text-2xl font-bold text-blue-600 dark:text-blue-400`}>
                        {integrityResults.warnings.length}
                      </p>
                      <p className={`text-sm ${themeStyles.text.muted}`}>Warnings</p>
                    </div>
                  </div>

                  {/* Error Details */}
                  {integrityResults.errors.length > 0 && (
                    <div className="mt-4">
                      <h4 className={`font-medium ${themeStyles.text.primary} mb-2`}>
                        Integrity Errors
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {integrityResults.errors.map((error, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-md ${getSeverityColor('error')}`}
                          >
                            <p className="font-medium">Log ID: {error.logId}</p>
                            <p className="text-sm">{error.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Warning Details */}
                  {integrityResults.warnings.length > 0 && (
                    <div className="mt-4">
                      <h4 className={`font-medium ${themeStyles.text.primary} mb-2`}>
                        Warnings
                      </h4>
                      <div className="space-y-2 max-h-40 overflow-y-auto">
                        {integrityResults.warnings.map((warning, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-md ${getSeverityColor('warning')}`}
                          >
                            <p className="font-medium">Log ID: {warning.logId}</p>
                            <p className="text-sm">{warning.message}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Suspicious Activity */}
              {securitySummary?.suspiciousActivity?.length > 0 && (
                <div className={`${themeStyles.bg.secondary} ${themeStyles.border.primary} rounded-lg border p-4`}>
                  <h3 className={`text-lg font-semibold ${themeStyles.text.primary} mb-4`}>
                    Suspicious Activity
                  </h3>
                  
                  <div className="space-y-3">
                    {securitySummary.suspiciousActivity.map((activity, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-md ${getSeverityColor('warning')}`}
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <AlertTriangle className="w-4 h-4" />
                          <span className="font-medium">
                            {activity.type.replace(/_/g, ' ').toUpperCase()}
                          </span>
                        </div>
                        <p className="text-sm">{activity.details}</p>
                        {activity.userId && (
                          <p className="text-xs mt-1">User: {activity.userId}</p>
                        )}
                        {activity.timestamp && (
                          <p className="text-xs">
                            Time: {new Date(activity.timestamp.toDate?.() || activity.timestamp).toLocaleString()}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Action Distribution */}
              {securitySummary?.actionTypes && (
                <div className={`${themeStyles.bg.secondary} ${themeStyles.border.primary} rounded-lg border p-4`}>
                  <h3 className={`text-lg font-semibold ${themeStyles.text.primary} mb-4`}>
                    Action Distribution
                  </h3>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {Object.entries(securitySummary.actionTypes).map(([action, count]) => (
                      <div key={action} className="text-center">
                        <p className={`text-xl font-bold ${themeStyles.text.primary}`}>
                          {count}
                        </p>
                        <p className={`text-sm ${themeStyles.text.muted}`}>
                          {action}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={`${themeStyles.bg.secondary} px-6 py-4 border-t ${themeStyles.border.primary}`}>
          <div className="flex justify-end">
            <button
              onClick={onClose}
              className={`px-4 py-2 rounded-md transition-colors ${themeStyles.button.secondary}`}
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SecurityDashboard;
